/**
 * Token resolution for headless rendering.
 *
 * The CSS source of truth is `arch.ui/tokens.css` (CSS custom props). For
 * headless rendering we need the same values as concrete numbers/colors
 * in JS. This is a hand-mirrored subset; keep it in sync with tokens.css.
 *
 * `color-mix(in oklab, …)` from the CSS is approximated here with an sRGB
 * mix — close enough for the kernel. An exact oklab port is a follow-up
 * (tracked in the plan doc).
 *
 * The TONE tokens (`accent`/`info`/`warn`/`err`/`ok`/`link`) are not mirrored
 * here at all — they read from `palette.ts`, the one table a theme retunes. Only
 * surfaces, ink and geometry are local.
 */

import { paletteColor } from "./palette.js";

export interface Tokens {
  readonly bg: string;
  readonly bgElev1: string;
  readonly bgElev2: string;
  readonly fg: string;
  readonly fgMuted: string;
  readonly fgSoft: string;
  readonly accent: string;
  readonly accentSoft: string;
  readonly radiusMd: number;
  readonly radiusSm: number;
  readonly radiusPill: number;
  readonly space1: number;
  readonly space2: number;
  readonly space3: number;
  readonly borderThin: number;
  readonly fontFamily: string;
  readonly fontMono: string;
  readonly fontSize: number;
  /** Divider / table-border color. */
  readonly border: string;
  /** Code-block / table-header background. */
  readonly codeBg: string;
  /** Link color for markdown anchors. */
  readonly link: string;
  /** Status tones — mirror `--au-info/-warn/-err/-ok` in arch.ui/tokens.css. */
  readonly info: string;
  readonly warn: string;
  readonly err: string;
  readonly ok: string;
}

const REM = 16;

export function defaultTokens(accent = paletteColor("accent")): Tokens {
  return {
    bg: "#0f172a",
    bgElev1: "#1e293b",
    bgElev2: "#334155",
    fg: "#e2e8f0",
    fgMuted: "#94a3b8",
    fgSoft: "#cbd5e1",
    accent,
    accentSoft: mix(accent, "#0f172a", 0.67), // ~33% accent over bg
    radiusMd: 12,
    radiusSm: 6,
    radiusPill: 9999,
    space1: 0.25 * REM,
    space2: 0.5 * REM,
    space3: 0.75 * REM,
    borderThin: 1,
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    fontMono: 'ui-monospace, "JetBrains Mono", Menlo, monospace',
    fontSize: 14,
    border: "#334155",
    // The tone tier comes FROM the palette rather than beside it. These were
    // five hexes byte-identical to the palette's roles, derived independently
    // from the same CSS vars — so a role retuned in one place stayed stale in
    // the other, and a data-classification chip could disagree with a node
    // painted `widget.color: warn`. (`err` keeps its short token name; the
    // palette role it reads is `error`.)
    // A surface, so it stays local — sourcing it from the `black` SWATCH would
    // mean retuning the colour an author writes as `widget.color: black` also
    // moved every code background.
    codeBg: "#0b1220",
    // The ROLE, not the `blue` swatch: same value today, but a link is
    // informational chrome, and retuning the swatch must not repaint links.
    link: paletteColor("info"),
    info: paletteColor("info"),
    warn: paletteColor("warn"),
    err: paletteColor("error"),
    ok: paletteColor("ok"),
  };
}

/** Parse #rgb / #rrggbb to [r,g,b] 0..255. */
function parseHex(hex: string): [number, number, number] {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

// ── OKLab color space (Björn Ottosson) — matches CSS `color-mix(in oklab)` ──

const srgbToLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const linearToSrgb = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

/** sRGB hex → OKLab [L, a, b]. */
function hexToOklab(hex: string): [number, number, number] {
  const [r8, g8, b8] = parseHex(hex);
  const r = srgbToLinear(r8 / 255);
  const g = srgbToLinear(g8 / 255);
  const b = srgbToLinear(b8 / 255);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** OKLab [L, a, b] → sRGB hex. */
function oklabToHex(L: number, A: number, B: number): string {
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  const r = linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s);
  const g = linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s);
  const b = linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s);
  return toHex(r * 255, g * 255, b * 255);
}

/** A hex color at `a` alpha (0..1) as an `rgba()` string. Translucent fills
 *  sit over whatever is behind them (the card's gradient), so a tinted chip
 *  reads lighter than the same tint mixed over the darkest base bg. */
export function alpha(hex: string, a: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${Math.round(a * 1000) / 1000})`;
}

/** Mix `a` toward `b` by `t` (0 = all a, 1 = all b) in OKLab — matches the
 *  CSS `color-mix(in oklab, …)` the live widgets use, so headless colors
 *  agree with the board. */
export function mix(a: string, b: string, t: number): string {
  const [aL, aA, aB] = hexToOklab(a);
  const [bL, bA, bB] = hexToOklab(b);
  return oklabToHex(aL + (bL - aL) * t, aA + (bA - aA) * t, aB + (bB - aB) * t);
}
